import { useEventStore } from '@/stores/eventStore'
import { useNotificationStore } from '@/stores/notificationStore'
import { useDeletingCourseVersion } from '@/views/components/button/deletingCourseVersionStore'
import { useCourseVersion } from '@/stores/courseVersionStore'

export enum EventType {
  NOTIFICATION = 'NOTIFICATION',
  CREATE_UPDATE = 'CREATE_UPDATE',
  GENERATE_REPORT = 'GENERATE_REPORT',
  REPORT_PROCESSING_COMPLETED = 'REPORT_PROCESSING_COMPLETED',
  DELETE_REGIONAL_COURSE_VERSION = 'DELETE_REGIONAL_COURSE_VERSION',
  IMPORT_REGIONAL_COURSE_VERSION = 'IMPORT_REGIONAL_COURSE_VERSION',
  NOTIFICATION_BADGE_UPDATE = 'NOTIFICATION_BADGE_UPDATE',
  COURSE_PLAN_GENERATION = 'COURSE_PLAN_GENERATION',
  AI_PROCESSING = 'AI_PROCESSING',
}

export const eventTypeMetadata: Record<EventType, { private: boolean }> = {
  [EventType.NOTIFICATION]: { private: false },
  [EventType.CREATE_UPDATE]: { private: true },
  [EventType.GENERATE_REPORT]: { private: true },
  [EventType.REPORT_PROCESSING_COMPLETED]: { private: true },
  [EventType.DELETE_REGIONAL_COURSE_VERSION]: { private: true },
  [EventType.IMPORT_REGIONAL_COURSE_VERSION]: { private: true },
  [EventType.NOTIFICATION_BADGE_UPDATE]: { private: true },
  [EventType.COURSE_PLAN_GENERATION]: { private: true },
  [EventType.AI_PROCESSING]: { private: true },
}

export const eventBus = ref(new EventTarget())

interface SSEEventData {
  eventId?: string
  status?: string
  [key: string]: unknown
}

const eventSource = shallowRef<EventSource | null>(null)

export const useSSE = () => {
  const eventStore = useEventStore()
  const deletingStore = useDeletingCourseVersion()

  const isLoggedIn = computed(() => !!useCookie('_logged_in').value)

  const replaceOrAddEvent = (
    type: EventType,
    eventId: string | undefined,
    data: string,
  ) => {
    if (eventId) {
      const idx = eventStore.events.findIndex((e) => {
        if (e.type !== type) return false
        try {
          const d = typeof e.data === 'string' ? JSON.parse(e.data) : e.data
          return d?.eventId === eventId
        } catch {
          return false
        }
      })
      if (idx !== -1) eventStore.events.splice(idx, 1)
    }
    eventStore.events.push({ type, data })
  }

  const startEventSource = () => {
    if (
      eventSource.value &&
      eventSource.value.readyState !== EventSource.CLOSED
    )
      return

    eventSource.value = new EventSource(
      `${import.meta.env.VITE_API_BASE_URL}/connect-events`,
      {
        withCredentials: true,
      },
    )

    eventSource.value.addEventListener(EventType.CREATE_UPDATE, (event) => {
      eventStore.events.push({
        type: EventType.CREATE_UPDATE,
        data: event.data,
      })
      eventBus.value.dispatchEvent(
        new CustomEvent(EventType.CREATE_UPDATE, {
          detail: JSON.parse(event.data),
        }),
      )
    })

    eventSource.value.addEventListener(
      EventType.REPORT_PROCESSING_COMPLETED,
      (event) => {
        eventStore.events.push({
          type: EventType.REPORT_PROCESSING_COMPLETED,
          data: event.data,
        })
        eventBus.value.dispatchEvent(
          new CustomEvent(EventType.REPORT_PROCESSING_COMPLETED, {
            detail: JSON.parse(event.data),
          }),
        )
      },
    )

    eventSource.value.addEventListener(
      EventType.DELETE_REGIONAL_COURSE_VERSION,
      (event) => {
        let parsed: SSEEventData
        try {
          parsed = JSON.parse(event.data)
        } catch {
          return
        }

        replaceOrAddEvent(
          EventType.DELETE_REGIONAL_COURSE_VERSION,
          parsed?.eventId,
          event.data,
        )

        const parts = (parsed?.eventId ?? '').split('-')
        const courseVersionId = parts.length >= 5 ? parts[4] : null

        if (parsed?.status === 'processing') {
          if (courseVersionId) deletingStore.startDeleting(courseVersionId)
        }

        if (parsed?.status === 'done' && courseVersionId) {
          deletingStore.stopDeleting(courseVersionId)

          const courseVersionStore = useCourseVersion()
          const allCourses = courseVersionStore.allCoursesInAreaStore

          courseVersionStore.allCoursesInAreaStore = allCourses.filter(
            (row: any) =>
              row.id?.toString() !== courseVersionId &&
              row.originCourseVersion?.versionedId?.toString() !==
                courseVersionId,
          )

          setTimeout(() => {
            const storeIdx = eventStore.events.findIndex((e) => {
              if (e.type !== EventType.DELETE_REGIONAL_COURSE_VERSION)
                return false
              try {
                const d =
                  typeof e.data === 'string' ? JSON.parse(e.data) : e.data
                return d?.eventId === parsed?.eventId
              } catch {
                return false
              }
            })
            if (storeIdx !== -1) eventStore.events.splice(storeIdx, 1)
          }, 3000)
        }

        if (parsed?.status === 'error' && courseVersionId) {
          deletingStore.stopDeleting(courseVersionId)

          setTimeout(() => {
            const storeIdx = eventStore.events.findIndex((e) => {
              if (e.type !== EventType.DELETE_REGIONAL_COURSE_VERSION)
                return false
              try {
                const d =
                  typeof e.data === 'string' ? JSON.parse(e.data) : e.data
                return d?.eventId === parsed?.eventId
              } catch {
                return false
              }
            })
            if (storeIdx !== -1) eventStore.events.splice(storeIdx, 1)
          }, 3000)
        }

        eventBus.value.dispatchEvent(
          new CustomEvent(EventType.DELETE_REGIONAL_COURSE_VERSION, {
            detail: parsed,
          }),
        )
      },
    )

    eventSource.value.addEventListener(
      EventType.IMPORT_REGIONAL_COURSE_VERSION,
      (event) => {
        let parsed: SSEEventData
        try {
          parsed = JSON.parse(event.data)
        } catch {
          return
        }

        replaceOrAddEvent(
          EventType.IMPORT_REGIONAL_COURSE_VERSION,
          parsed?.eventId,
          event.data,
        )

        if (parsed?.status === 'done' || parsed?.status === 'error') {
          setTimeout(() => {
            const idx = eventStore.events.findIndex((e) => {
              if (e.type !== EventType.IMPORT_REGIONAL_COURSE_VERSION)
                return false
              try {
                const d =
                  typeof e.data === 'string' ? JSON.parse(e.data) : e.data
                return d?.eventId === parsed?.eventId
              } catch {
                return false
              }
            })
            if (idx !== -1) eventStore.events.splice(idx, 1)
          }, 3000)
        }

        eventBus.value.dispatchEvent(
          new CustomEvent(EventType.IMPORT_REGIONAL_COURSE_VERSION, {
            detail: parsed,
          }),
        )
      },
    )

    eventSource.value.addEventListener(
      EventType.COURSE_PLAN_GENERATION,
      (event) => {
        let parsed: SSEEventData
        try {
          parsed = JSON.parse(event.data)
        } catch {
          return
        }

        // Backend envia o payload do plano em `message` (JSON string)
        let detail: SSEEventData = parsed
        if (typeof parsed.message === 'string') {
          try {
            const inner = JSON.parse(parsed.message) as SSEEventData
            detail = {
              ...parsed,
              ...inner,
              status: (inner.status as string) || parsed.status,
            }
          } catch {
            // mantém parsed
          }
        }

        replaceOrAddEvent(
          EventType.COURSE_PLAN_GENERATION,
          detail?.eventId ?? parsed?.eventId,
          JSON.stringify(detail),
        )

        const terminalStatuses = ['done', 'error', 'completed', 'failed', 'replaced']
        if (terminalStatuses.includes(String(detail?.status))) {
          setTimeout(() => {
            const idx = eventStore.events.findIndex((e) => {
              if (e.type !== EventType.COURSE_PLAN_GENERATION) return false
              try {
                const d =
                  typeof e.data === 'string' ? JSON.parse(e.data) : e.data
                return d?.eventId === (detail?.eventId ?? parsed?.eventId)
              } catch {
                return false
              }
            })
            if (idx !== -1) eventStore.events.splice(idx, 1)
          }, 3000)
        }

        eventBus.value.dispatchEvent(
          new CustomEvent(EventType.COURSE_PLAN_GENERATION, {
            detail,
          }),
        )
      },
    )

    eventSource.value.addEventListener(
      EventType.AI_PROCESSING,
      (event) => {
        let parsed: SSEEventData
        try {
          parsed = JSON.parse(event.data)
        } catch {
          return
        }

        // Backend envia o payload de negócio em `message` (JSON string)
        let detail: SSEEventData = parsed
        if (typeof parsed.message === 'string') {
          try {
            const inner = JSON.parse(parsed.message) as SSEEventData
            detail = {
              ...parsed,
              ...inner,
              status: (inner.status as string) || parsed.status,
            }
          } catch {
            // mantém parsed
          }
        }

        replaceOrAddEvent(
          EventType.AI_PROCESSING,
          detail?.eventId ?? parsed?.eventId,
          JSON.stringify(detail),
        )

        const terminalStatuses = [
          'done',
          'error',
          'COMPLETED',
          'FAILED',
        ]
        if (terminalStatuses.includes(String(detail?.status))) {
          setTimeout(() => {
            const idx = eventStore.events.findIndex((e) => {
              if (e.type !== EventType.AI_PROCESSING) return false
              try {
                const d =
                  typeof e.data === 'string' ? JSON.parse(e.data) : e.data

                return d?.eventId === (detail?.eventId ?? parsed?.eventId)
              } catch {
                return false
              }
            })
            if (idx !== -1) eventStore.events.splice(idx, 1)
          }, 3000)
        }

        eventBus.value.dispatchEvent(
          new CustomEvent(EventType.AI_PROCESSING, {
            detail,
          }),
        )
      },
    )

    eventSource.value.addEventListener(
      EventType.NOTIFICATION_BADGE_UPDATE,
      (event) => {
        try {
          const parsed = JSON.parse(event.data)
          const message =
            typeof parsed.message === 'string'
              ? JSON.parse(parsed.message)
              : parsed.message
          const notificationStore = useNotificationStore()

          notificationStore.setUnreadCount(message.count)
        } catch {
          // silently ignore malformed events
        }
      },
    )

    eventSource.value.onopen = () => {
      eventSource.value!.addEventListener('ping', () => {})

      useNotificationStore().fetchUnreadCount()

      setTimeout(() => {
        const hasActiveDeleteEvents = eventStore.events.some((e) => {
          if (e.type !== EventType.DELETE_REGIONAL_COURSE_VERSION) return false
          try {
            const d = typeof e.data === 'string' ? JSON.parse(e.data) : e.data
            return d?.status === 'processing'
          } catch {
            return false
          }
        })
        if (!hasActiveDeleteEvents) deletingStore.clearAll()
      }, 3000)

      // Limpa eventos de exclusão que ficaram presos em 'processing' por mais de 3 minutos
      setTimeout(() => {
        const now = Date.now()
        const staleThresholdMs = 3 * 60 * 1000

        const staleEvents = eventStore.events.filter((e) => {
          if (e.type !== EventType.DELETE_REGIONAL_COURSE_VERSION) return false
          try {
            const d = typeof e.data === 'string' ? JSON.parse(e.data) : e.data
            if (d?.status !== 'processing') return false
            const parts = (d?.eventId ?? '').split('-')
            const timestamp =
              parts.length > 0 ? parseInt(parts[parts.length - 1], 10) : NaN
            return !isNaN(timestamp) && now - timestamp > staleThresholdMs
          } catch {
            return false
          }
        })

        if (staleEvents.length > 0) {
          staleEvents.forEach((e) => eventStore.removeEvent(e))
          deletingStore.clearAll()
        }

        const staleImportEvents = eventStore.events.filter((e) => {
          if (e.type !== EventType.IMPORT_REGIONAL_COURSE_VERSION) return false
          try {
            const d = typeof e.data === 'string' ? JSON.parse(e.data) : e.data
            if (d?.status === 'done' || d?.status === 'error') return true
            if (d?.status !== 'processing') return false
            const parts = (d?.eventId ?? '').split('-')
            const timestamp =
              parts.length > 0 ? parseInt(parts[parts.length - 1], 10) : NaN
            return !isNaN(timestamp) && now - timestamp > staleThresholdMs
          } catch {
            return false
          }
        })

        if (staleImportEvents.length > 0)
          staleImportEvents.forEach((e) => eventStore.removeEvent(e))

        const staleCoursePlanEvents = eventStore.events.filter((e) => {
          if (e.type !== EventType.COURSE_PLAN_GENERATION) return false
          try {
            const d = typeof e.data === 'string' ? JSON.parse(e.data) : e.data
            if (['done', 'error', 'completed', 'failed', 'replaced'].includes(d?.status))
              return true
            if (d?.status !== 'processing' && d?.status !== 'pending') return false
            const parts = (d?.eventId ?? '').split('-')
            const timestamp =
              parts.length > 0 ? parseInt(parts[parts.length - 1], 10) : NaN
            return !isNaN(timestamp) && now - timestamp > staleThresholdMs
          } catch {
            return false
          }
        })

        if (staleCoursePlanEvents.length > 0)
          staleCoursePlanEvents.forEach((e) => eventStore.removeEvent(e))
      }, 5000)
    }
  }

  const stopEventSource = () => {
    if (eventSource.value) {
      eventSource.value.close()
      eventSource.value = null
    }
  }

  watch(
    isLoggedIn,
    (loggedIn) => {
      if (loggedIn) startEventSource()
      else stopEventSource()
    },
    { immediate: true },
  )

  return {
    events: eventStore.events,
  }
}
